/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Globe2, Loader2, Download, AlertTriangle, Ban } from "lucide-react";

const money = (n, d) => `${Number(n || 0).toFixed(2)} ${d === "GBP" ? "£" : d === "EUR" ? "€" : d}`;
const NIVEAU = { ok: ["bg-[#5CB85C]/15 text-[#2d7a2d]", "Conforme"], attention: ["bg-[#D4AF5A]/20 text-[#8A6D2B]", "Attention"], alerte: ["bg-[#E0702E]/20 text-[#9a4a16]", "Alerte 20 %"], bloque: ["bg-[#D9534F]/15 text-[#b23a36]", "Bloqué"], na: ["bg-[#F5F3EE] text-[#0A1426]/60", "n/a"] };

export default function PaysDashboardPage() {
  const [d, setD] = useState(null);
  const [pays, setPays] = useState("");
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/telesoin/reseau/pays${pays ? `?pays=${pays}` : ""}`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail))), [pays]);
  useEffect(() => { load(); }, [load]);
  const csv = async () => {
    try { const r = await api.get(`/telesoin/reseau/pays?format=csv${pays ? `&pays=${pays}` : ""}`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `reseau-pays-${d.mois}.csv`; a.click(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (err) return <div data-testid="pays-forbidden" className="bc-in rounded-3xl bg-[#D9534F]/10 border border-[#D9534F]/30 p-5 text-[#b23a36] text-sm">{err}</div>;
  if (!d) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;
  return (
    <div className="bc-in flex flex-col gap-5" data-testid="pays-dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-[Fraunces] text-2xl md:text-3xl font-bold text-[#0A1426] flex items-center gap-2"><Globe2 className="text-[#2BA3A0]" /> Réseau par pays · {d.mois}</h1>
          <p className="text-[13px] text-[#0A1426]/70 mt-1">Vue du centre racine : praticiens groupés par pays, actes et montants du mois, séances vidéo, ratio 20 % (France) et alertes de seuil.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="pays-filter" value={pays} onChange={(e) => setPays(e.target.value)} className="h-10 px-3 rounded-full border border-[#E4E0D5] text-[13px] text-[#0A1426] bg-white">
            <option value="">Tous les pays</option>
            {d.pays_disponibles.map((p) => <option key={p.code} value={p.code}>{`${p.drapeau} ${p.nom}`}</option>)}
          </select>
          <Button data-testid="pays-csv-btn" onClick={csv} className="h-10 rounded-full bg-[#0A1426] text-white text-[12px] font-bold px-4"><Download size={14} className="mr-1" /> CSV</Button>
        </div>
      </div>

      <div data-testid="pays-groupes" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {d.groupes.map((g) => (
          <div key={g.pays} data-testid={`pays-groupe-${g.pays}`} className="bg-white rounded-3xl border border-[#E4E0D5] p-4">
            <div className="flex items-center justify-between"><div className="font-[Fraunces] text-lg font-bold text-[#0A1426]">{g.drapeau} {g.pays_nom}</div>{g.alertes > 0 && <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-[#D9534F] text-white inline-flex items-center gap-1"><AlertTriangle size={12} /> {g.alertes} alerte(s)</span>}</div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-[12px] text-[#0A1426]">
              <div><div className="font-bold text-lg">{g.praticiens}</div>praticien(s)</div>
              <div><div className="font-bold text-lg">{g.patients}</div>patients</div>
              <div><div className="font-bold text-lg">{g.actes_mois}</div>actes du mois</div>
              <div><div className="font-bold text-lg">{money(g.montant_mois, g.devise)}</div>montant du mois</div>
              <div className="col-span-2"><b>{g.seances_video_mois}</b> séance(s) vidéo ce mois</div>
            </div>
          </div>
        ))}
        {d.groupes.length === 0 && <div className="text-[13px] text-[#0A1426]/60">Aucun praticien pour ce filtre.</div>}
      </div>

      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5 overflow-x-auto" data-testid="pays-table-card">
        <table className="w-full text-[12px] text-[#0A1426]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#0A1426]/60"><th className="py-2 pr-2">Pays</th><th className="pr-2">Praticien</th><th className="pr-2">Centre</th><th className="pr-2">Patients</th><th className="pr-2">Actes (mois)</th><th className="pr-2">Montant</th><th className="pr-2">Vidéo</th><th className="pr-2">Ratio 12 m</th><th>Niveau</th></tr></thead>
          <tbody>
            {d.praticiens.map((l) => (
              <tr key={l.praticien_id} data-testid={`pays-row-${l.praticien_id}`} className="border-t border-[#F5F3EE]">
                <td className="py-2 pr-2">{l.drapeau} {l.pays_nom}</td>
                <td className="pr-2 font-bold"><Link to="/pro/reseau" className="hover:underline">{l.praticien}</Link>{l.expert_facturation && <span className="ml-1 text-[10px] font-bold text-[#2BA3A0]">expert</span>}</td>
                <td className="pr-2">{l.centre || "—"}{l.ville ? ` · ${l.ville}` : ""}</td>
                <td className="pr-2">{l.patients}</td>
                <td className="pr-2">{l.actes_mois} <span className="text-[#0A1426]/50">({l.distance_mois} dist.)</span></td>
                <td className="pr-2 font-bold">{money(l.montant_mois, l.devise)}</td>
                <td className="pr-2">{l.seances_video_mois}</td>
                <td className="pr-2">{l.ratio_12m_pct != null ? `${l.ratio_12m_pct} %` : "n/a"}</td>
                <td><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${NIVEAU[l.niveau][0]}`}>{l.niveau === "bloque" && <Ban size={11} />}{NIVEAU[l.niveau][1]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
